/**
 * Initialize the canvas for drawing.
 *
 * @param {Object} dom - The DOM elements for the canvas module.
 * @param {Object} state - The state object for the canvas module.
 * @param {Function} checkCanvasHasContent - Function to check if the canvas has content.
 *
 * @returns {void}
 */
export default function initCanvas({
	dom,
	state,
	checkCanvasHasContent,
	pushState,
	deactivateCursorAccessory,
}) {
	const { canvas, accessoryContainer, startPoint, wrapper } = dom;
	if (!canvas) return;

	const ctx = canvas.getContext('2d');
	const tileImage = new Image();
	tileImage.src = wrapper.getAttribute('data-drawing-tile');

	const maxBrushSize = 60;
	const minBrushSize = 10;
	let isDrawing = false;
	let isColoring = false;

	/**
	 * Resize the canvas and accessory container.
	 *
	 * @returns {void}
	 */
	const resizeCanvas = () => {
		const container = canvas.parentElement;
		if (container.clientHeight < window.innerHeight) {
			container.style.height = `${window.innerHeight}px`;
		}
		canvas.width = container.clientWidth;
		canvas.height = container.clientHeight;
		accessoryContainer.style.width = `${canvas.width}px`;
		accessoryContainer.style.height = `${canvas.height}px`;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
	};

	/**
	 * Check if there is hair at the given point on the canvas.
	 *
	 * @param {number} x - The x-coordinate of the point to check.
	 * @param {number} y - The y-coordinate of the point to check.
	 *
	 * @returns {boolean} - True if there is hair at the point, false otherwise.
	 */
	const isHairAtPoint = (x, y) => {
		const p = ctx.getImageData(x, y, 1, 1).data;
		return p[3] > 0; // Check alpha channel
	};

	/**
	 * Draw on the canvas.
	 *
	 * @param {Object} params - The parameters for the draw function.
	 * @param {number} params.offsetX - The x-coordinate of the pointer event.
	 * @param {number} params.offsetY - The y-coordinate of the pointer event.
	 * @param {number} params.movementX - The change in x-coordinate since the last event.
	 * @param {number} params.movementY - The change in y-coordinate since the last event.
	 *
	 * @returns {void}
	 */
	const draw = ({ offsetX, offsetY, movementX, movementY }) => {
		const distances = [offsetX, canvas.width - offsetX, offsetY, canvas.height - offsetY];
		const minDist = Math.min(...distances);
		const brushSize =
			minDist < 100 ? minBrushSize + (minDist / 100) * (maxBrushSize - minBrushSize) : maxBrushSize;
		const x = offsetX - brushSize / 2;
		const y = offsetY - brushSize / 2;
		const angle = Math.atan2(movementY, movementX);

		// Draw original and mirrored tiles
		[1, -1].forEach((scale) => {
			ctx.save();
			ctx.translate(
				scale === 1 ? x + brushSize / 2 : canvas.width - x - brushSize / 2,
				y + brushSize / 2,
			);
			ctx.rotate(angle);
			if (scale === -1) ctx.scale(-1, 1);
			ctx.drawImage(tileImage, -brushSize / 2, -brushSize / 2, brushSize, brushSize);
			ctx.restore();
		});
	};

	/**
	 * Fill the area around the pointer.
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const fillArea = (e) => {
		// Only color if the cursor is over existing hair
		if (!isHairAtPoint(e.offsetX, e.offsetY)) return;

		const airbrushRadius = 75; // How wide the brush is.
		const airbrushAlpha = 0.15; // How transparent the paint is (0=transparent, 1=opaque).

		// Convert the current drawing color to a transparent version for the gradient edge.
		// This robustly handles any color format (e.g., 'red', '#F00', 'rgb(255,0,0)').
		const tempCtx = document.createElement('canvas').getContext('2d');
		tempCtx.fillStyle = state.drawingColor;
		tempCtx.fillRect(0, 0, 1, 1);
		const [r, g, b] = tempCtx.getImageData(0, 0, 1, 1).data;
		const transparentColor = `rgba(${r}, ${g}, ${b}, 0)`;

		// Use an offscreen canvas for better performance and for the masking effect
		const offscreenCanvas = document.createElement('canvas');
		offscreenCanvas.width = canvas.width;
		offscreenCanvas.height = canvas.height;
		const octx = offscreenCanvas.getContext('2d');

		// Create a soft, circular gradient
		const grad = octx.createRadialGradient(
			e.offsetX,
			e.offsetY,
			0,
			e.offsetX,
			e.offsetY,
			airbrushRadius,
		);
		// The gradient now fades from the solid color to a transparent version of itself
		grad.addColorStop(0, state.drawingColor);
		grad.addColorStop(1, transparentColor);

		octx.fillStyle = grad;
		octx.globalAlpha = airbrushAlpha;

		// Draw the gradient circle
		octx.beginPath();
		octx.arc(e.offsetX, e.offsetY, airbrushRadius, 0, Math.PI * 2);
		octx.fill();

		// This uses the existing hair drawing as a mask, so the color only appears on the hair
		octx.globalCompositeOperation = 'destination-in';
		octx.drawImage(canvas, 0, 0);

		// Draw the final result onto the main canvas
		ctx.drawImage(offscreenCanvas, 0, 0);
	};

	/**
	 * Place an accessory on the canvas.
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const placeAccessory = (e) => {
		if (!isHairAtPoint(e.offsetX, e.offsetY)) return;
		pushState();

		const wRect = accessoryContainer.getBoundingClientRect();
		const relX = e.clientX - wRect.left;
		const relY = e.clientY - wRect.top;

		const imgEl = new Image();
		imgEl.draggable = false;
		imgEl.src = state.selectedAccessory;
		imgEl.setAttribute('data-placed-accessory', 'true');
		imgEl.classList.add('wp-block-mavis-design-a-hairstyle__canvas-accessory');
		if (
			document
				.querySelector('.wp-block-mavis-design-a-hairstyle__cursor-accessory')
				?.classList.contains('flipped')
		) {
			imgEl.classList.add('flipped');
		}

		Object.assign(imgEl.style, {
			position: 'absolute',
			width: `${state.currentAccessorySize}px`,
			height: 'auto',
			zIndex: '3',
		});
		accessoryContainer.appendChild(imgEl);

		/**
		 * Set the position of the accessory image once it has loaded.
		 *
		 * @returns {void}
		 */
		imgEl.onload = () => {
			imgEl.style.left = `${relX - imgEl.offsetWidth / 2}px`;
			imgEl.style.top = `${relY - imgEl.offsetHeight / 2}px`;
			checkCanvasHasContent();
		};

		deactivateCursorAccessory();
		// eslint-disable-next-line no-param-reassign
		state.selectedAccessory = null;
	};

	/**
	 * Handle pointer movement while drawing.
	 *
	 * @param {PointerEvent} evt - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const onPointerMoveDraw = (evt) => {
		if (!isDrawing) return;
		const rect = canvas.getBoundingClientRect();
		draw({
			offsetX: evt.clientX - rect.left,
			offsetY: evt.clientY - rect.top,
			movementX: evt.movementX,
			movementY: evt.movementY,
		});
	};

	/**
	 * Handle pointer up events while drawing.
	 *
	 * @returns {void}
	 */
	const onPointerUpDraw = () => {
		isDrawing = false;
		document.removeEventListener('pointermove', onPointerMoveDraw);
		document.removeEventListener('pointerup', onPointerUpDraw);
		checkCanvasHasContent();
	};

	/**
	 * Handle pointer down events.
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const onPointerDown = (e) => {
		if (state.currentMode === 'drawing') {
			const onStart = startPoint.contains(e.target);
			const onHair = isHairAtPoint(e.offsetX, e.offsetY);
			if (!onStart && !onHair) return;

			pushState();
			isDrawing = true;
			const rect = canvas.getBoundingClientRect();
			draw({
				offsetX: e.clientX - rect.left,
				offsetY: e.clientY - rect.top,
				movementX: 0,
				movementY: 0,
			});
			document.addEventListener('pointermove', onPointerMoveDraw);
			document.addEventListener('pointerup', onPointerUpDraw);
		} else if (state.currentMode === 'coloring') {
			if (!isHairAtPoint(e.offsetX, e.offsetY)) return;
			pushState();
			isColoring = true;
			fillArea(e);
			canvas.setPointerCapture(e.pointerId);
		}
	};

	/**
	 * tileImage onload event handler.
	 *
	 * @returns {void}
	 */
	tileImage.onload = () => {
		startPoint.addEventListener('pointerdown', onPointerDown);
		canvas.addEventListener('pointerdown', onPointerDown);

		canvas.addEventListener('click', (e) => {
			if (state.currentMode === 'accessorizing' && state.selectedAccessory) {
				placeAccessory(e);
			} else if (state.currentMode === 'coloring') {
				fillArea(e); // Allow click-to-fill too
			}
		});

		canvas.addEventListener('pointermove', (e) => {
			if (!isColoring || state.currentMode !== 'coloring' || !isHairAtPoint(e.offsetX, e.offsetY))
				return;
			fillArea(e);
		});

		/**
		 * Stop coloring on pointerup or cancel
		 *
		 * @returns {void}
		 */
		const stopColoring = () => {
			isColoring = false;
		};
		canvas.addEventListener('pointerup', stopColoring);
		canvas.addEventListener('pointercancel', stopColoring);
	};

	window.addEventListener('load', resizeCanvas);
	resizeCanvas();
}
